'use strict'

Vue.component('awe-avails-tab-view', {
  props: {
    defaultTab: {
      type: String,
      default: 'online',
      validator(value) {
        return ['online', 'offline', 'custom'].includes(value);
      },
    },
    showOfflineBuyBtn: {type: Boolean, default: true},
    showCustomTab: {type: Boolean, default: false},

    // tab header labels
    onlineTabLabel: {type: String, default: ''},
    offlineTabLabel: {type: String, default: ''},
    customTabLabel: {type: String, default: ''},

    // availabilities lists
    offlineAvails: {type: Array, default: () => {return []}},
    onlineAvails: {type: Array, default: () => {return []}},
    customAvails: {type: Array, default: () => {return null}},

    onlineOnly: {type: Boolean, default: false},
    displayPrices: {type: Boolean, default: true},
    multiOffer: {type: Boolean, default: false},
    ready: {type: Boolean, default: false},
    showRetailerHomeLink: {type: Boolean, default: false},
    offlineGroups: {type: Map, default: () => {return null}},

    // customTabOrder <= 0 : custom tab goes first
    // customTabOrder === 1 : goes between online and offline
    // customTabOrder >= 2 : goes last
    customTabOrder:{type:Number, default: 0},

    showRetailerText: {type: Boolean, default: false},
    showLocalRetailers: {type: Boolean, default: null},

    // custom texts
    noOfflineStoresText: {type: String, default: ''},
    hiddenStoresText: {type: String, default: ''},

    // Custom message for no online store returned.
    onlineNoStoreLabel: {type: String, default: ''},
  },
  data: function(){
    return {
      i18n: window.i18n,
      selectedTab: this.defaultTab,
      error: '',
      app: app,
    }
  },
  watch: {
    defaultTab: {
      handler: function(newVal) {
        this.selectedTab = newVal;
      },
      immediate: true,
    },
    onlineAvails: function(){
      this.checkForTabSwitch()
    },
    customAvails: function(){
      this.checkForTabSwitch()
    },
  },
  computed:{
    // Defines order in which tab headers are rendered.
    // Only applies to labels, it does not make sense to order tab contents
    tabOrder: function(){  
      const tabOrder = ['online', 'offline']

      if (this.showCustomTab)
        tabOrder.splice(this.customTabOrder, 0, 'custom')

      return tabOrder
    },
  },
  created: function(){
    // on opening with scope change, reset current tab to default.
    this.$root.$on('scope-update', evt => {
      if (evt.opening)
        this.selectedTab = this.defaultTab
    })
  },
  methods: {
    showNoStoresMessage: function(storeAvailability) {
      return storeAvailability.length === 0 && this.ready && this.error != 'UNAVAILABLE_COUNTRY_ERROR'
    },
    showStoreHours: function(item) {
      return (item.nearestStore || item.store).openingHours || (item.nearestStore || item.store).open24;
    },
    getImgUrl: function(item) {
      return `https://content.static-swaven.com/img/retailers/hd/${item.retailer.logoId}.png`;
    },
    tabClickHandler: function(evt) {
      const tab = evt.currentTarget.dataset.tab
      this.selectedTab = tab

      if (tab === 'offline')
        app.showMap(null, false, true)

      Tracker.dropTag('clktab', {tab: tab})
    },
    shouldShowItemPrice: function(item) {
      if (this.selectedTab === 'online') {
        return this.displayPrices && item.price.canShow;
      } else {
        return this.displayPrices
          && item.price.canShow
          && !this.multiOffer
          && this.showOfflineBuyBtn
          && item.clkUrl;
      }
    },
    getAriaLabel: function(tag, item, getStoreFullName) {
      let name = item?.store.name;
      if (getStoreFullName) {
        name = Utils.getStoreFullname(item);
      }
      return i18n.getString(tag, 'name', name);
    },
    // Returns tab label for given tab. Takes custom text or localized message.
    getTabLabel: function(tab) {
      let value = ''
      switch(tab) {
        case 'online':
          value = this.onlineTabLabel || i18n.getString('FIND_ONLINE')
          break
        case 'offline':
          value = this.offlineTabLabel || i18n.getString('FIND_STORE')
          break
        case 'custom':
          value = this.customTabLabel || i18n.getString('FIND_CUSTOM')
          break
      }
      return value
    },
    getDomainFromUrl(item) {
      const itemURL = item.store.url || item.retailer.url
      if(itemURL) {
        const url = new URL(itemURL)
        const m = url.hostname.match(/^(?:[^\.\s]+\.)?(?<host>[^\.\s]+(?:\.\w{2,})+)$/m)
        const domain = m ? m.groups.host : url.hostname
        return domain
      }

      return ''
    },
    showNestedAvails: function(avail) {
      // Ignore child avails without clkUrl
      return this.multiOffer
        && this.offlineGroups?.get(avail.store.id)?.some(x => !!x.clkUrl)
    },
    // check if there are results for the current tab. If not and offline list has results,
    // switches to the offline tab.
    checkForTabSwitch: function(list, name) {
      switch(this.selectedTab) {
        case 'online':
          if (this.onlineAvails.length === 0 && this.offlineAvails.length > 0 && !this.onlineOnly){
            this.selectedTab = 'offline'
            if (!app.$refs.map.$data._mapboxLoaded)
              app.showMap(null, false, true)
          }
          break
        case 'custom':
          if (this.customAvails.length === 0) {
            if (this.onlineAvails.length > 0)
              this.selectedTab = 'online'
            else if (this.offlineAvails.length > 0 && !this.onlineOnly){
              this.selectedTab = 'offline'
              if (!app.$refs.map.$data._mapboxLoaded)
                app.showMap(null, false, true)
            }
          }
          break
      }
    },
  },

  template: `
    <div class="stores avails-tab-view" :class="[this.selectedTab, {'alcohol-msg': showLocalRetailers != null && offlineAvails.length === 0}]" ref="availsTabView">
      <div id="tabs" v-if="!onlineOnly" role="tablist" :aria-activedescendant="this.selectedTab + '-tab'">
        <template v-for="tab in tabOrder">
          <button :id="tab+'-tab'"
              class="tab" role="tab"
              v-on:click="tabClickHandler" :data-tab="tab" tabindex="0"
              :aria-label="getTabLabel(tab)"
              :aria-selected="(selectedTab === tab).toString()"
              :aria-controls="tab+'-stores'">

            <slot :name="tab+'-tab-icon'"></slot>
            
            <span class="label">{{ getTabLabel(tab) }}</span>
          </button>
        </template>
      </div>

      <div class="store-ctnr" v-bind:class="{'single-tab': onlineOnly}">

      <slot name="address-bar"></slot>

        <slot name="map">
          <div class="missing-map">NO MAP HERE</div>
        </slot>

        <div
          id="online-stores"
          class="online-view"
          v-show="this.selectedTab === 'online'"
          role="tabpanel"
        >
          <div class="list-ctnr" role="list">
            <div v-if="showNoStoresMessage(onlineAvails)" class="error" role="dialog">{{onlineNoStoreLabel || i18n.getString('NOSTORE_ONLINE')}}</div>
            <div v-else-if="error" class="error" role="dialog">
             <span>{{i18n.getString(error)}}</span>
            </div>
            <div v-else v-for="item in onlineAvails" role="listitem" class="row _avail" :data-sid="item.store.id">
              <div class="img-ctnr">
                <img v-if="!showRetailerText" class="retailer-logo" :src="getImgUrl(item)" :alt="item.retailer.name">
                <span v-else class="retailer-name">{{ item.retailer.name || item.store.name }}</span>
              </div>
              <div class="pkg" v-if="multiOffer">
                {{item.product.pkg}}
              </div>
              <div class="price" v-if="shouldShowItemPrice(item)">
                {{i18n.formatPrice(item?.price?.amount, item?.price?.currency)}}
              </div>
              <a
                v-on:click="app.redirect($event, item)"
                :href="item.clkUrl"
                :aria-disabled="!item.clkUrl"
                :aria-label="getAriaLabel('BUY_LBL', item)"
                class="btn"
                target="_blank"
              >
                <span>{{i18n.getString('BUY')}}</span>
                <svg width="14px" height="14px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <use href="#external-link" />
                </svg>
              </a>
            </div>
          </div>
          <awe-loader ref="loader_avails_next_1" type="avails-next" class="inline"></awe-loader>
        </div>


        <div
          id="offline-stores"
          v-if="!onlineOnly"
          class="offline"
          v-show="this.selectedTab === 'offline'"
          role="tabpanel"
        >
          <div class="list-ctnr" role="list">
            <div v-if="showNoStoresMessage(offlineAvails) && showLocalRetailers" class="alcohol-custom">
              {{ noOfflineStoresText || i18n.getString('ALCOHOL_NO_OFFLINE') }}
            </div>
            <div v-else-if="showNoStoresMessage(offlineAvails) && showLocalRetailers === false" class="alcohol-custom">
              {{ hiddenStoresText || i18n.getString('ALCOHOL_HIDDEN_STORES') }}
            </div>
            <div v-else-if="showNoStoresMessage(offlineAvails)" class="error" role="dialog">
              {{ i18n.getString('NOSTORE_OFFLINE') }}
            </div>
            <div v-else-if="error" class="error" role="dialog">
              <span>{{i18n.getString(error)}}</span>
            </div>

            <div v-else v-for="item in offlineAvails"
              :data-store-id="item.store.id"
              @click="app.showMap(item)" class="row _avail" role="listitem"
              :data-sid="item.store.id">

              <div class="logo">
                <div v-if="!showRetailerText" class="img-ctnr">
                <img :src="getImgUrl(item)" :alt="item.retailer.name">
                </div>
                <span v-else class="retailer-name">
                  {{item.retailer.name || item.store.name}}
                </span>
                <div class="price" v-if="shouldShowItemPrice(item)">
                  {{i18n.formatPrice(item.price.amount, item.price.currency)}}
                </div>
              </div>

              <div class="address">
                <div class="dist">{{i18n.formatDistance(item.store.distance, true)}}</div>
                <div v-if="!item.retailer.name || item.retailer.isDefaultLogo" class="name">{{item.store.name}}</div>
                <div v-if="item.store.location.address">{{item.store.location.address}}</div>
                <span v-if="item.store.location.city">{{item.store.location.city}}</span>
                <span v-if="item.store.location.zipCode || item.store.location.state">{{i18n.formatZipCodeState(item.store)}}</span>
              </div>

              <div v-if="item.store.phone" class="phone">
                <!-- <span class="icon-phone"></span> -->
                <a :href="'tel:+'+ item.store.phone"
                  @click="$root.dropTag($event, 'clkloc', item, {auto: false, clkloc_type: 'tel'})"
                  :aria-label="getAriaLabel('PHONE_LBL', item, true)"
                  target="_top"
                  >
                  {{item.store.phone}}
                </a>
              </div>
            
              <div class="buttons">
                <div
                  class="btn route"
                  v-on:click="app.showDirections(item)"
                  @keydown.enter="app.showDirections(item)"
                  tabindex="0"
                  role="button"
                  :aria-label="getAriaLabel('SHOWROUTE_LBL', item, true)"
                >

                  <span>{{i18n.getString('SHOWROUTE')}}</span>
                </div>
                <a
                  v-if="!multiOffer && showOfflineBuyBtn && item.clkUrl"
                  v-on:click="app.redirect($event, item)"
                  :href="item.clkUrl"
                  :aria-disabled="!item.clkUrl"
                  :aria-label="getAriaLabel('BUY_LBL', item)"
                  target="_blank"
                  class="buy-btn"
                  v-bind:class="{ disabled: !item.clkUrl }"
                >
                  <span>{{i18n.getString('BUY')}}</span>
                
                  <svg width="14px" height="14px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <use href="#external-link" />
                  </svg>
                </a>
                <a
                  v-if="showRetailerHomeLink && (item.store.url || item.retailer.url)"
                  v-on:click="app.redirect($event, item)"
                  data-redirect-mode="retailerHome"
                  :href="item.store.url || item.retailer.url"
                  :aria-label="i18n.getString('RETAIL_HOME_REDIR_LBL', 'retailerDomain', getDomainFromUrl(item))"
                  target="_blank"
                  class="buy-btn"
                >
                  <span>{{getDomainFromUrl(item)}}</span>
                  <svg width="14px" height="14px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <use href="#external-link" />
                  </svg>
                </a>
              </div>

              <awe-opening-hours v-if="showStoreHours(item)"
                :hours="(item.nearestStore || item.store).openingHours"
                :timezone="(item.nearestStore || item.store).timezone"
                :open24="(item.nearestStore || item.store).open24"
              >
              </awe-opening-hours>

              <awe-multi-offers v-if="showNestedAvails(item)" class="nested-avails"
                :avails="offlineGroups.get(item.store.id).filter(x => x.clkUrl)"
                :show-offline-buy-btn="showOfflineBuyBtn"
                :show-prices="displayPrices"
              >              
              </awe-multi-offers>
            </div>
          </div>
          <awe-loader ref="loader_avails_next_1" type="avails-next" class="inline"></awe-loader>
        </div>

        <!-- 3rd tab for custom list -->
        <div id="custom-stores"
          v-if="showCustomTab"
          v-show="selectedTab === 'custom'"
          class="online-view" role="tabpanel"
        >
        <div class="list-ctnr" role="list">
          <div v-if="error" class="error" role="dialog">
            <span>{{i18n.getString(error)}}</span>
          </div>
          <div v-else v-for="item in customAvails" role="listitem" class="row _avail" :data-sid="item.store.id">
            <div class="img-ctnr">
              <img class="retailer-logo" :src="getImgUrl(item)" :alt="item?.retailer?.name">
            </div>
            <div class="pkg" v-if="multiOffer">
              {{item.product.pkg}}
            </div>
            <div class="price" v-if="shouldShowItemPrice(item)">
              {{i18n.formatPrice(item?.price?.amount, item?.price?.currency)}}
            </div>
            <a
              v-on:click="app.redirect($event, item)"
              :href="item.clkUrl"
              :aria-disabled="!item.clkUrl"
              :aria-label="getAriaLabel('BUY_LBL', item)"
              class="btn"
              target="_blank"
            >
              <span>{{i18n.getString('BUY')}}</span>
              <svg width="14px" height="14px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
               <use href="#external-link" />
              </svg>
            </a>
          </div>
        </div>
        <awe-loader ref="loader_avails_next_1" type="avails-next" class="inline"></awe-loader>
        
        </div>

       
      </div>
      <svg width="0" height="0" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <g id="external-link">  
            <path d="m18 23h-14c-1.654 0-3-1.346-3-3v-14c0-1.654 1.346-3 3-3h8c.552 0 1 .447 1 1s-.448 1-1 1h-8c-.551 0-1 .448-1 1v14c0 .552.449 1 1 1h14c.551 0 1-.448 1-1v-8c0-.553.448-1 1-1s1 .447 1 1v8c0 1.654-1.346 3-3 3z"/>
            <path d="m22 1h-6c-.404 0-.769.243-.924.617s-.069.804.217 1.09l2.293 2.293-7.293 7.293c-.391.391-.391 1.023 0 1.414.195.195.451.293.707.293s.512-.098.707-.293l7.293-7.293 2.293 2.293c.191.191.447.293.707.293.129 0 .259-.024.383-.076.373-.154.617-.52.617-.924v-6c0-.553-.448-1-1-1z"/>
          </g>
          <g id="toggle">
            <path d="M21 1a2.1213 2.6517 45 013 3L14 14a2 2.1 0 01-3 0L1 4a2.6517 2.1213 45 013.0988-2.9012l8.5 8.5z"/>
          </g>
        </defs>
      </svg>
    </div>
  `
})

